import type IAnalyticsConfig from './plugins/analytics/IAnalyticsConfig'
import type { IApplicationInsightsConfig } from '@/plugins/app-insights'
import type { IConfigurationOptions } from '@/plugins/configurations'
import type { IServicesOptions } from '@/plugins/services'
import { register } from '@ignite-systems/auth-service-web-components'
import Buefy from 'buefy'
import { createPinia, PiniaVuePlugin } from 'pinia'
import { v4 as uuidv4 } from 'uuid'
import Vue, { watch } from 'vue'
import Vue2TouchEvents from 'vue2-touch-events'
import AnalyticsPlugin from '@/plugins/analytics/analytics'
import ApplicationInsightsPlugin from '@/plugins/app-insights'
import ChatPlugin from '@/plugins/chat'
import ConfigurationPlugin from '@/plugins/configurations'
import DirectivesPlugin from '@/plugins/directives'
import EventBusPlugin from '@/plugins/event-bus'
import JourneyTransitionsPlugin from '@/plugins/journey-transitions'
import { initLD } from '@/plugins/launchdarkly'
import PWAUtilsPlugin from '@/plugins/pwa-utils'
import ServicesPlugin from '@/plugins/services'
import UtilsPlugin from '@/plugins/utils'
import TenantEnum from '@/services/enums/TenantEnum'
import App from './App.vue'
import Routes from './constants/Routes'
import CloudflareTurnstilePlugin from './plugins/cloudflare-turnstile'
import PaymentGatewayPlugin from './plugins/payment-gateway'
import ThreeDSecurePlugin from './plugins/three-d-secure'
import router from './router'
import TractiveUtils from './utils/TractiveUtils'

register()

async function main() {
  const processTenant = `${window.injectedEnv.FRONTEND_TENANT}` as TenantEnum

  if (processTenant === TenantEnum.Tractive)
    await new TractiveUtils().handleBrandSwitching()

  switch (processTenant) {
    case TenantEnum.Yoga:
    case TenantEnum.Switch:
    case TenantEnum.Tractive:
    case TenantEnum.Zoomcover:
    case TenantEnum.Dash:
    case TenantEnum.Quotax:
    case TenantEnum.Zen:
    case TenantEnum.Demo02:
    case TenantEnum.Ignite:
      await import(`@/styles/${processTenant}/global.scss`)
      break
    case TenantEnum.Hagerty:
    case TenantEnum.KeeyPartners:
    default:
      await import(`@/styles-legacy/${processTenant}/global.scss`)
      break
  }

  Vue.config.productionTip = false

  const appInsightsConfig: IApplicationInsightsConfig = {
    config: {
      instrumentationKey: 'd802ee99-d605-4106-b056-f7dd89ed9a40',
      appId: '94a48f07-24ce-460f-af5c-c8ad6d05a3f7',
      enableUnhandledPromiseRejectionTracking: true,
      enableAjaxErrorStatusText: true,
    },
    router,
  }

  Vue.use(ApplicationInsightsPlugin, appInsightsConfig)

  const analyticsConfig: IAnalyticsConfig = {
    gtm: window.injectedEnv.FRONTEND_GTM,
    analytics: window.injectedEnv.FRONTEND_GA,
    router,
    tenant: processTenant,
  }
  // Initialize LaunchDarkly for session replay and observability
  initLD()

  Vue.use(AnalyticsPlugin, analyticsConfig)

  let apiUrl = new URL(window.injectedEnv.FRONTEND_API_URL)
  // HACK: portal is mostly deprecated now so this will die with the ignite-portal repo...
  if (window.location.hostname === 'portal.onedayinsurance.co.uk')
    apiUrl = new URL('https://api.onedayinsurance.co.uk/api')

  const servicesOptions: IServicesOptions = {
    apiUrl,
    axiosConfig: {
      withCredentials: true,
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache',
        'Expires': '0',
      },
    },
    tenant: processTenant,
    router,
  }

  const configurationOptions: IConfigurationOptions = {
    tenant: processTenant,
  }

  Vue.use(Buefy)
  Vue.use(ConfigurationPlugin, configurationOptions)

  Vue.use(ServicesPlugin, servicesOptions)
  Vue.use(ChatPlugin)
  Vue.use(EventBusPlugin)
  Vue.use(UtilsPlugin, servicesOptions.tenant)
  Vue.use(DirectivesPlugin)
  Vue.use(PWAUtilsPlugin)
  Vue.use(Vue2TouchEvents)
  Vue.use(JourneyTransitionsPlugin)
  Vue.use(ThreeDSecurePlugin)
  Vue.use(PaymentGatewayPlugin)
  Vue.use(CloudflareTurnstilePlugin)

  Vue.config.ignoredElements = [
    'ignite-auth-service-login',
    'ignite-auth-service-register-button',
    'ignite-auth-service-login-buttons',
    'ignite-auth-service-forgot-button',
  ]

  Vue.use(PiniaVuePlugin)
  const pinia = createPinia()

  const piniaStateCreatedRaw = localStorage.getItem('piniaStateCreated')
  let piniaStateCreated: number | null = null

  if (piniaStateCreatedRaw)
    piniaStateCreated = Date.parse(piniaStateCreatedRaw)

  const expiryTime = 2 * 60 * 60 * 1000 // expire two hours

  let localPiniaState: string | null = null

  if (piniaStateCreated && (new Date()).getTime() - piniaStateCreated < expiryTime)
    localPiniaState = localStorage.getItem('piniaState')
  else
    localStorage.removeItem('piniaState')

  pinia.state.value = localPiniaState ? JSON.parse(localPiniaState) : {}
  localStorage.setItem('piniaStateCreated', (new Date()).toUTCString())

  watch(
    pinia.state,
    (state) => {
      // persist the whole state to the local storage whenever it changes
      localStorage.setItem('piniaState', JSON.stringify(state))
      localStorage.setItem('piniaStateCreated', (new Date()).toUTCString())
    },
    { deep: true },
  )

  // kick users to session expired if another vue instance changes logged in account
  window.instanceId = uuidv4()
  localStorage.removeItem('authChangedBy')
  setInterval(() => {
    const authChangedBy = localStorage.getItem('authChangedBy')
    if (authChangedBy && authChangedBy !== window.instanceId && router.currentRoute.name !== Routes.sessionExpired) {
      router.replace({
        name: Routes.sessionExpired,
      })
    }
  }, 1000)

  new Vue({
    router,
    pinia,
    render: h => h(App),
  }).$mount('#app')
}

main()
